import React, { memo, useState, useEffect, useMemo } from 'react';
import { enableES5 } from 'immer';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import moment from 'moment';
import classNames from 'classnames';
import xhr from '../../../utils/xhr';
import utils from '../../../utils/utils';
import { showNotification } from '../../../redux/notification';
import BaseModal from '../BaseModal/BaseModal';
import ShiftsRequiredByDate from '../../../features/shiftsToRequest/ShiftsRequiredByDate';
import { user } from '../../../redux/user';
import FlexRequestShiftConfirmationModal from '../FlexRequestShiftConfirmationModal/FlexRequestShiftConfirmationModal';
import facilityInfoIcon from '../../../assets/images/facility-info-icon.svg';
import Select from '../../form/Select';
import useStateWithCallback from '../../../hooks/useStateWithCallback';
import useRequestShifts from '../../../hooks/useRequestShifts';
import useMediaQuery, { MEDIUM_SCREEN_WIDTH_QUERY } from '../../hooks/useMediaQuery';
import ConfirmationModal from '../ConfirmationModal';
import ShiftsConfirmation from '../../../features/shiftsToRequest/ShiftsConfirmation';
import PERMISSION from '../../../constants/Permissions';
import LoadingSpinner from '../../spinner/LoadingSpinner';

import './SelectFacilityRequestShiftsModal.css';

enableES5();

const SelectFacilityRequestShiftsModal = (props) => {
	const userData = useSelector(user);
	const { permissions, permissionsByFacility } = userData;
	const dispatch = useDispatch();

	const isMobile = useMediaQuery(MEDIUM_SCREEN_WIDTH_QUERY);
	const [selectedFacility, setSelectedFacility] = useState();
	const [showLoadingSpinner, setShowLoadingSpinner] = useStateWithCallback(false);
	const [showChangingFacilityModal, setShowChangingFacilityModal] = useState(false);
	const [facilityToLoad, setFacilityToLoad] = useState();
	const [currentShiftStructures, setCurrentShiftStructures] = useState([]);
	const [currentFacilityUnits, setCurrentFacilityUnits] = useState([]);

	const { shiftStructures, isGlobalCalendar } = props;

	const {
		initialShiftGroups,
		shiftsGroups,
		setShiftsGroups,
		errors,
		handleBackendErrors,
		updateShiftGroupDate,
		addEmptyShift,
		updateShift,
		removeShift,
		addEmptyShiftGroup,
		getAllowRemove,
		showConfirmationModal,
		setShowConfirmationModal,
		showFlexStructuresAlreadyStartedModal,
		setShowFlexStructuresAlreadyStartedModal,
		checkIntegrity,
		getShiftsToRequest
	} = useRequestShifts(userData, currentShiftStructures);

	const postShiftsRequested = async () => {
		setShowLoadingSpinner(true, async () => {
			const currentFacility = selectedFacility.id;
			try {
				props.setIsLoading(true);
				let postUrl = '/shifts';
				const shiftsToRequest = getShiftsToRequest(shiftsGroups, props.isFlex);
				let payload = {
					facilityId: currentFacility,
					shiftsData: shiftsToRequest
				};
				if (props.isFlex) {
					postUrl = `/flex/facility/${currentFacility}/shifts`;
					payload = shiftsToRequest;
				}
				const shiftsPosted = await xhr.request('POST', postUrl, payload);
				if (shiftsPosted) {
					dispatch(showNotification('success', 'Shifts requested successfully'));
					props.updateFeed();
					props.onClose();
				}
			} catch (e) {
				if (e.response.status === 403) {
					dispatch(
						showNotification(
							'error',
							'Shift could not be added as you do not have permission to add shifts at this facility.'
						)
					);
					props.onClose();
				} else {
					const errorMessage = utils.handleNetworkErrorMessage(e, 'Failed to save requested shifts. Please try again.');

					if (e.response && e.response.data) {
						handleBackendErrors(e.response.data.errors);
					}

					dispatch(showNotification('error', errorMessage));
				}
			} finally {
				setShowConfirmationModal(false);
				setShowLoadingSpinner(false);
				props.setIsLoading(false);
			}
		});
	};

	const triggerRecurringShiftsModal = () => {
		if (props.user.isRecurringScheduleEnabled) {
			props.onClose();
			props.setShowRecurringShiftsModal(true);
		} else {
			dispatch(
				showNotification(
					'error',
					'This feature is coming soon. If you would like early access, please email PortalSupport@ShiftMed.com'
				)
			);
		}
		return true; // placeholder for sonarcloud
	};

	const showUnits = useMemo(() => {
		if (!currentFacilityUnits?.length) {
			return false;
		}

		// if the facility doesn't have portal units enabled, permissions don't matter anymore
		if (!selectedFacility?.portalUnitsEnabled) {
			return false;
		}

		if (isGlobalCalendar) {
			const facilityPermissions = permissionsByFacility[selectedFacility?.id];
			return utils.isAuthorizedExact(
				props.isFlex ? PERMISSION.FLEX_ASSIGN_UNITS : PERMISSION.EDIT_ASSIGNMENT,
				facilityPermissions
			);
		}

		return utils.isAuthorizedExact(
			props.isFlex ? PERMISSION.FLEX_ASSIGN_UNITS : PERMISSION.EDIT_ASSIGNMENT,
			permissions
		);
	}, [permissions, selectedFacility, permissionsByFacility, props.isFlex]);

	const handleChangeFacility = () => {
		setShiftsGroups(initialShiftGroups);
		const facility = props.facilities.find((f) => f.value === facilityToLoad);
		setSelectedFacility(facility);
		setCurrentShiftStructures(shiftStructures.filter((structure) => structure.facility_id === facilityToLoad));
		setCurrentFacilityUnits(facility.units || []);
		setFacilityToLoad(null);
		setShowChangingFacilityModal(false);
	};

	const handleSelectFacility = (e) => {
		const existChanges = shiftsGroups.some((sg) => sg.shifts.some((s) => !!s.resourceType));
		const currentFacility = selectedFacility?.id;
		if (currentFacility && currentFacility !== e.facility && existChanges) {
			setShowChangingFacilityModal(true);
			setFacilityToLoad(e.facility);
		} else {
			const facility = props.facilities.find((f) => f.value === e.facility);
			setCurrentShiftStructures(shiftStructures.filter((structure) => structure.facility_id === e.facility));
			setCurrentFacilityUnits(facility.units);
			setSelectedFacility(facility);
		}
	};

	useEffect(() => {
		if (props.facilities.length === 1) {
			const [activeFacility] = props.facilities;
			setSelectedFacility(activeFacility);
			setCurrentFacilityUnits(activeFacility.units || []);
			setCurrentShiftStructures(shiftStructures.filter((structure) => structure.facility_id === activeFacility.value));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.facilities]);

	return [
		<Modal
			key="select-facility-shift-requests-modal"
			size="xl"
			show={!showConfirmationModal && !showFlexStructuresAlreadyStartedModal && !showChangingFacilityModal}
			aria-labelledby="contained-modal-title-vcenter"
			centered
			className="select-facility-shift-requests-modal"
			onHide={props.onClose}
			backdrop="static"
			keyboard={false}
		>
			<Modal.Header closeButton className="modal-request-header">
				<Modal.Title id="contained-modal-title-vcenter" className="request-shift-title">
					{props.title || 'Single shifts'}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="body-request-modal">
				{isGlobalCalendar && (
					<div className="select-facility-container">
						<div className="facility-label">
							Facility
							<OverlayTrigger
								className="tooltip-component"
								placement="right"
								delay={{ show: 100, hide: 400 }}
								overlay={
									<Tooltip delay={{ show: 150, hide: 400 }} className="info-facility-tooltip">
										This list will only include facilities where you have permission to request shifts. If the facility
										you need isn’t listed, please reach out to your account manager.
									</Tooltip>
								}
							>
								<img src={facilityInfoIcon} alt="info" />
							</OverlayTrigger>
						</div>
						<div className="second-label">Requests will only apply to the selected facility.</div>
						<div style={{ width: '504px', marginTop: '4px' }}>
							<Select
								containerClass="select-facility"
								name="facility"
								placeholder="Select Facility"
								className="select-facility-request-shift"
								isSearchable
								options={props.facilities}
								value={selectedFacility?.id}
								handleChange={handleSelectFacility}
								filterFn={(option, input) => {
									if (input) {
										return option.label.toLowerCase().includes(input.toLowerCase());
									}
									return true;
								}}
							/>
						</div>
					</div>
				)}
				{shiftsGroups.map((shiftsGroup, index) => (
					<ShiftsRequiredByDate
						// eslint-disable-next-line react/no-array-index-key
						key={`${moment(shiftsGroup.date).format('YYYY-MM-DD')}-${index}`}
						shiftStructures={currentShiftStructures}
						updateShiftGroupDate={updateShiftGroupDate}
						shiftsGroup={shiftsGroup}
						shiftGroupIndex={index}
						allowRemove={getAllowRemove()}
						addEmptyShift={addEmptyShift}
						updateShift={updateShift}
						removeShift={removeShift}
						errors={errors}
						isOddIndex={index % 2 !== 0}
						isFlex={props.isFlex}
						addShiftsButton="+ Add another shift"
						showOnlyFirstRowLabels
						currentFacility={selectedFacility?.id}
						facilityPortalUnitsEnabled={selectedFacility?.portalUnitsEnabled}
						showUnits={showUnits}
						units={currentFacilityUnits}
						isGlobalCalendar={isGlobalCalendar}
						isMobile={isMobile}
						isFirstGroup={index === 0}
					/>
				))}
				<div className={classNames('add-another-date-container', { 'no-border': shiftsGroups.length % 2 === 0 })}>
					<button onClick={addEmptyShiftGroup} className="add-date-button" type="button">
						+ Add another date
					</button>
				</div>
			</Modal.Body>
			<Modal.Footer>
				{!props.isFlex && !isGlobalCalendar && (
					<Button className="add-recurring-shifts-button" onClick={triggerRecurringShiftsModal} variant="secondary">
						<b>+</b> Add a recurring shift
					</Button>
				)}
				<div className="buttons">
					<Button className="cancel-shifts-button" onClick={props.onClose} variant="secondary">
						{props.cancelLabel || 'Cancel'}
					</Button>
					<Button className="req-shifts-button" onClick={() => checkIntegrity(true)} variant="primary">
						{props.confirmLabel || 'Request Shifts'}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>,

		showConfirmationModal && props.isFlex && (
			<BaseModal
				confirmModal
				className="primary-colors rounded-modal"
				key="confirmation-modal-request-flex"
				title="Request Shifts?"
				primaryActionLabel="Request"
				onPrimaryAction={postShiftsRequested}
				secondaryActionLabel="Go Back"
				onSecondaryAction={() => setShowConfirmationModal(false)}
				onHide={() => setShowConfirmationModal(false)}
				primaryActionDisabled={showLoadingSpinner}
				secondaryActionDisabled={showLoadingSpinner}
			>
				Do you want to confirm this shift request?
			</BaseModal>
		),

		showConfirmationModal && !props.isFlex && (
			<ConfirmationModal
				className="confirmation-modal-request"
				key="confirmation-modal-request"
				size="xl"
				title="Confirm Request"
				scrollable
				customModalBodyClass="modal-body-shift-confirmation"
				onConfirm={postShiftsRequested}
				onCancel={() => setShowConfirmationModal(false)}
				showLoadingSpinner={false}
				closeButton={false}
				cancelLabel="Go Back"
				confirmLabel="Confirm"
			>
				{showLoadingSpinner && (
					<div className="spinner-wrapper">
						<LoadingSpinner key="loading-spinner" />
					</div>
				)}
				<div className="facility-and-modal-container">
					<span>
						Facility: <span className="facility-name">{selectedFacility?.label || ''}</span>
					</span>
					<ShiftsConfirmation
						shiftsGroups={shiftsGroups}
						shiftStructures={shiftStructures}
						shiftsByDay={[]}
						facilityId={selectedFacility?.id}
						showUnits={showUnits}
					/>
				</div>
			</ConfirmationModal>
		),

		showFlexStructuresAlreadyStartedModal && (
			<FlexRequestShiftConfirmationModal
				key="confirmation-modal-request-flex"
				title="Request shifts? Shifts have started."
				textContent="One or more of the shifts you selected have already started. Do you want to confirm this shift request?"
				boxTextContent={
					<>
						<strong>Reminder:</strong> Shift modifications must be made before their scheduled end time
					</>
				}
				primaryActionLabel="Request shifts"
				onPrimaryAction={postShiftsRequested}
				secondaryActionLabel="Go Back"
				onSecondaryAction={() => setShowFlexStructuresAlreadyStartedModal(false)}
				onHide={() => setShowFlexStructuresAlreadyStartedModal(false)}
				primaryActionDisabled={showLoadingSpinner}
				secondaryActionDisabled={showLoadingSpinner}
			/>
		),

		showChangingFacilityModal && (
			<FlexRequestShiftConfirmationModal
				key="confirmation-modal-request-flex"
				title="Change Facility?"
				textContent={
					<>
						<div>Your shift request has not been completed.</div>
						<div className="change-facility-secondary-text">
							If you switch to a different facility now, your current shift request <strong>will be discarded</strong>.
						</div>
					</>
				}
				primaryActionLabel="Change Facility"
				onPrimaryAction={handleChangeFacility}
				secondaryActionLabel="Go Back"
				onSecondaryAction={() => setShowChangingFacilityModal(false)}
				onHide={() => setShowChangingFacilityModal(false)}
				primaryActionDisabled={showLoadingSpinner}
				secondaryActionDisabled={showLoadingSpinner}
				secundaryButtons
			/>
		)
	];
};

export default memo(SelectFacilityRequestShiftsModal);
